import { useMemo } from 'react';
import formatDistance from 'date-fns/formatDistance';
import { twJoin } from 'tailwind-merge';

import { AccessLevelIcon, ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { ColumnDef, Icon, Link, RyeconLineage, Tooltip } from '@dbt-labs/sourdough';

import { AccessLevel, FieldChanged } from '@/__generated__/graphql';
import { ResourceChip } from '@/components/ResourceChip';
import { CustomCell } from '@/components/Table/CustomCell';
import { OwnerHeader } from '@/components/Table/OwnerHeader';
import { TwoLineHeader } from '@/components/Table/TableHeader';
import { RecentResourceChangeResponse } from '@/hooks/discovery/recentResourceChanges';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { useCurrentTime } from '@/util/date';
import { snakeToSentenceCase } from '@/util/string';

function getName(resource: RecentResourceChangeResponse['resource']) {
  if (!resource) return null;
  if ('name' in resource) return resource.name;
  return null;
}

function getAccess(resource: RecentResourceChangeResponse['resource']) {
  if (!resource) return null;
  if ('access' in resource) return resource.access;
  return null;
}

function getNameCellData(data: RecentResourceChangeResponse) {
  return {
    name: getName(data.resource) ?? data.uniqueId,
    uniqueId: data.uniqueId,
    access: getAccess(data.resource),
    isDeleted: isDeleted(data),
  };
}

function isDeleted(data: RecentResourceChangeResponse) {
  return data.changes.includes(FieldChanged.DeletedResource);
}

type NameCellData = ReturnType<typeof getNameCellData>;

type LatestUpdatesParams = {
  now?: Date;
  disableGroupsColumn: boolean;
};

export const useLatestChangesColumns = ({
  now,
  disableGroupsColumn,
}: LatestUpdatesParams) => {
  const getLineageLink = useLinkToExplorer('projectLineage');
  const { accountId, projectId, environmentType } = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const currentTime = useCurrentTime(now ? () => now : undefined);

  const columns = useMemo((): ColumnDef<RecentResourceChangeResponse>[] => {
    return [
      {
        header: () => <TwoLineHeader title="Name" />,
        id: 'name',
        accessorFn: getNameCellData,
        cell: ({ getValue }) => {
          const { name, uniqueId, access, isDeleted } = getValue() as NameCellData;
          return (
            <>
              <span className="flex w-full items-center">
                {
                  <AccessLevelIcon
                    className={twJoin(
                      'mr-2 mt-0.5 h-4',
                      isDeleted ? 'text-fgDecorative' : null,
                    )}
                    access={access as AccessLevel}
                    tooltipConfig={{ placement: 'right' }}
                  />
                }
                {isDeleted && (
                  <span className="italic text-fgDecorative" data-testid="nolink">
                    {name}
                  </span>
                )}
                {!isDeleted && (
                  <Tooltip content={name} displayOnlyWhenTruncated className="w-full">
                    {(ref) => (
                      <div ref={ref} className="w-full truncate">
                        <Link
                          isInternal
                          to={getDetailsPageLink({
                            accountId,
                            environmentType,
                            projectId,
                            dbtUniqueId: uniqueId,
                          })}
                        >
                          {name}
                        </Link>
                      </div>
                    )}
                  </Tooltip>
                )}
              </span>
            </>
          );
        },
      },
      {
        accessorKey: 'resource.resourceType',
        header: () => <TwoLineHeader title="Resource" />,
        minSize: 155,
        size: 10,
        cell: ({ getValue }) => (
          <span className="space-x-1 whitespace-nowrap">
            <ResourceChip resourceType={getValue() as ResourceTypeExplorer} />
          </span>
        ),
      },
      {
        accessorKey: 'changes',
        accessorFn: ({ changes }) => {
          return snakeToSentenceCase(changes.join(', '));
        },
        header: () => <TwoLineHeader title="Changed properties" />,
        cell: (props) => {
          const changes = props.getValue() as string;
          return <CustomCell tooltip={changes}>{changes}</CustomCell>;
        },
      },
      {
        accessorKey: 'group',
        accessorFn: (data) => {
          const resource = data.resource;
          return {
            uniqueId: data.uniqueId,
            group: resource ? ('group' in resource ? resource.group : '') : '',
          };
        },
        header: () => {
          return <OwnerHeader />;
        },
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          const group = value.group;
          const parsedResourceUniqueId = parseUniqueId(value.uniqueId);
          const groupUniqueId = `group.${parsedResourceUniqueId.packageName}.${group}`;
          const url = getDetailsPageLink({
            accountId,
            environmentType,
            projectId,
            dbtUniqueId: groupUniqueId,
          });
          return (
            <CustomCell
              disable={disableGroupsColumn}
              tooltip={group}
              children={
                <Link isInternal to={url}>
                  {group}
                </Link>
              }
            />
          );
        },
        size: 100,
      },
      {
        accessorKey: 'mostRecentChangedAt',
        accessorFn: ({ mostRecentChangedAt }) => {
          const formatted = formatDistance(new Date(mostRecentChangedAt), currentTime, {
            addSuffix: true,
          });
          if (formatted === 'less than a minute ago') return 'just now';
          return formatted;
        },
        header: () => <TwoLineHeader title="Last updated" />,
        size: 100,
        maxSize: 145,
      },
      {
        accessorKey: 'uniqueId',
        accessorFn: (data) => ({
          uniqueId: data.uniqueId,
          isDeleted: isDeleted(data),
          filePath: data.resource?.filePath,
        }),
        header: () => <TwoLineHeader title="Lineage" />,
        maxSize: 70,
        minSize: 70,
        size: 46,
        cell: (cellContext) => {
          const value = cellContext.getValue() as any;
          const filePath: string | null = value.filePath ?? null;
          const isDeleted: boolean = value.isDeleted;
          const uniqueId: string = value.uniqueId;

          if (!filePath || isDeleted) return null;
          return (
            <Link
              className="mx-auto"
              isInternal
              to={getLineageLink({
                dbtUniqueId: uniqueId,
                accountId,
                environmentType,
                projectId,
                // NOTE(https://dbtlabs.atlassian.net/browse/META-3360): FQN is not yet exposed on the ...on Resource interface
                selector: `2+${filePath}+2`,
              })}
            >
              <Icon ryecon={RyeconLineage} />
            </Link>
          );
        },
      },
    ];
  }, [
    accountId,
    currentTime,
    disableGroupsColumn,
    environmentType,
    getDetailsPageLink,
    getLineageLink,
    projectId,
  ]);
  return columns;
};
